import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { ChevronIcon } from '@sainsburys-tech/bolt-icons'
import { Button, AddToTrolley, ProductCard, Drawer } from '@sainsburys-tech/bolt'
import { useVocab } from '@sainsburys-tech/bolt-vocab-provider'
import { isEmpty } from '../../../helpers/utils'
import Image from '../../../helpers/Image/Image'
import { C002DrawerProps } from './types'
import { onQuickViewSlidingPanelOpen } from '../../functional/UGC/ugc.tracking'
import {
  StyledDrawerContent,
  StyledDrawerImageContainer,
  StyledImageLabel,
  StyledProductContainer,
  StyledButtonContainer,
  StyledButtonContainerMobile,
  StyledProductCardContainer,
  StyledChevronContainer,
  StyledNoProducts,
  StyledProductCardGrid,
  StyledProductsHeader,
  StyledChevronButton
} from './C002Drawer.styles'
import { fetcher } from '../../../helpers/ComponentHelper'
import { productPanelOnClick } from '../../functional/UGC/ugc.helpers.js'

const C002Drawer = ({
  heading,
  displayImage,
  toggleCallback,
  channel,
  carouselCallback,
  drawerInitialLoad
}: C002DrawerProps): React.ReactElement => {
  if (!displayImage) return null

  const { ITEM_CONTAINER } = useVocab()
  const [productsData, setProducts] = useState({ products: [], included: [] })
  const [isLoading, setLoading] = useState<boolean>(true)
  const resolveProductDetails = (productResults) => {
    if (productResults.data) {
      const { data, included } = productResults
      const productObject = {
        products: Array.isArray(data) ? data : [data],
        included: Array.isArray(included) ? included : [included]
      }
      return ReactDOM.unstable_batchedUpdates(() => {
        setProducts(productObject)
        setLoading(false)
        if (drawerInitialLoad) {
          onQuickViewSlidingPanelOpen(
            productObject.products,
            productObject.included,
            {
              type: 'socialQuickview',
              currentTab: 'iWantToShop',
              clickOrigin: 'ugcComponent',
              eventName: 'quickviewLbPage'
            },
            window
          )
        }
      })
    }
    return null
  }
  const fetchProductData = async (productIds: Array<string>) => {
    try {
      const partNumbers = productIds?.join(',')
      const options = {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/vnd.argos-product.v2+json' },
        credentials: 'include'
      }
      const url = `/product-api/pdp-service/partNumber/${partNumbers}?returnMeta=true`
      const { json, status, ok } = await fetcher(url, options)
      if (!ok) {
        throw new Error(`There has been an error with (${status})`)
      }
      return resolveProductDetails(json)
    } catch (err) {
      console.error(err)
      return ReactDOM.unstable_batchedUpdates(() => {
        setProducts({ products: [], included: [] })
        setLoading(false)
      })
    }
  }

  useEffect(() => {
    if (!isEmpty(displayImage?.productIds)) {
      fetchProductData(displayImage?.productIds)
    }
  }, [displayImage])

  const addToTrolleyButtonOptions = (isProductCard, id = '') => {
    const attTracking = AddToTrolley.onAddToTrolleyModalOpen([{ id }])('socialQuickviewLbATT')
    const mappedIds = displayImage?.productIds?.map((newId) => ({ id: newId }))
    const attTrackingMultipleIds = AddToTrolley.onAddToTrolleyModalOpen(mappedIds)('socialQuickviewLbATT')
    return {
      partNumber: isProductCard ? id : displayImage?.productIds,
      name: isProductCard ? 'Add to trolley' : 'all items',
      full: !isProductCard,
      onClick: !isProductCard
        ? () => {
            toggleCallback()
            attTrackingMultipleIds()
          }
        : () => attTracking(),
      'data-test': 'c002-add-to-trolley',
      openView: true,
      disableRecommendations: true,
      disableWarranties: true,
      kind: isProductCard ? 'secondary' : 'primary'
    }
  }

  const displayError = isEmpty(productsData.products)

  const renderProductCards = () => {
    if (isLoading) {
      return null
    }
    if (displayError) {
      return (
        <StyledNoProducts data-test='oops-message'>
          <p>Oops there are currently no products to show</p>
          <Button
            data-test='similar-ideas-button'
            kind='secondary'
            onClick={() => carouselCallback(displayImage.index, 'next')}
          >
            Shop products for similar ideas
          </Button>
        </StyledNoProducts>
      )
    }
    return productsData.products.map((product, index) => {
      const { id } = product
      const { name } = product.attributes
      const reviews = productsData.included.find((inc) => inc.type === 'reviewstatistics' && inc.id === id)
      const { attributes: reviewAttributes } = reviews
      const prices = productsData.included.find((inc) => inc.type === 'prices' && inc.id === id)
      const { attributes: price } = prices

      const reviewRating = reviewAttributes?.avgRating || null
      return (
        <StyledProductCardContainer key={`pp-${id}-${index}`} data-test={`c002-product-card-${index}`}>
          <ProductCard
            partNumber={id}
            onClick={() => productPanelOnClick(id, channel)}
            isHighlighted={product.isHighlighted}
            mobileLayout='portrait'
            name={name}
            price={price.now}
            wasPrice={price.was}
            rating={reviewRating}
            reviewsCount={reviewAttributes?.reviewCount}
            cta={
              <AddToTrolley.Button
                data-test={`c002-add-to-trolley-${index}-button`}
                {...addToTrolleyButtonOptions(true, id)}
                style={{ padding: '12px' }}
              />
            }
          />
        </StyledProductCardContainer>
      )
    })
  }
  return (
    <Drawer data-test='c002-drawer' heading={heading} show={!isEmpty(displayImage)} size='lg' onClose={toggleCallback}>
      <StyledDrawerContent data-test='c002-drawer-content'>
        <StyledDrawerImageContainer aria-live='polite' isSainsburys={false}>
          <StyledChevronContainer dir='left' isSainsburys={false}>
            <StyledChevronButton onClick={() => carouselCallback(displayImage?.index, 'previous')}>
              <ChevronIcon aria-label='Previous image' data-test='chevron-left' dir='left' width='2em' height='2em' />
            </StyledChevronButton>
          </StyledChevronContainer>
          <Image
            data-test={`c002-drawer-main-image-${displayImage?.index}`}
            src={displayImage?.urlLarge}
            alt={`Image by @${displayImage?.author} with ${productsData?.products.length} shoppable products posted on ${displayImage?.timestamp}`}
            cdn={false}
          />
          <StyledChevronContainer isSainsburys={false}>
            <StyledChevronButton onClick={() => carouselCallback(displayImage.index, 'next')}>
              <ChevronIcon aria-label='Next image' data-test='chevron-right' dir='right' width='2em' height='2em' />
            </StyledChevronButton>
          </StyledChevronContainer>
          <StyledImageLabel>
            <p data-test='c002-drawer-author'>{`@${displayImage?.author}`}</p>
            <p data-test='c002-drawer-date'>{`${displayImage?.timestamp}`}</p>
          </StyledImageLabel>
        </StyledDrawerImageContainer>
        {!displayError && (
          <StyledButtonContainerMobile>
            <AddToTrolley.Button {...addToTrolleyButtonOptions(false, '')}>
              Add all items to {ITEM_CONTAINER}
            </AddToTrolley.Button>
          </StyledButtonContainerMobile>
        )}
        <StyledProductContainer data-test='c002-product-container' isError={displayError}>
          {!displayError && <StyledProductsHeader>Shop the look</StyledProductsHeader>}
          <StyledProductCardGrid isError={displayError}>{renderProductCards()}</StyledProductCardGrid>
        </StyledProductContainer>
      </StyledDrawerContent>
      {!displayError && (
        <StyledButtonContainer>
          <AddToTrolley.Button {...addToTrolleyButtonOptions(false)}>
            Add all items to {ITEM_CONTAINER}
          </AddToTrolley.Button>
        </StyledButtonContainer>
      )}
    </Drawer>
  )
}

export default C002Drawer
